import { Settings } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import SettingsStats from 'components/SettingsStats/SettingsStats';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { useEffect, useState } from 'react';
import * as setup from 'setup';
import { useLocalStorage } from 'usehooks-ts';

export default function SettingsControls() {
    const [debugs, setDebugs] = useState<SettingsControlsInput[]>([]);
    const [eventlessTestNames, setEventlessTestNames] = useState<string[] | null>(null);
    const [eventTestNames, setEventTestNames] = useState<string[] | null>(null);
    const [open, setOpen] = useLocalStorage('pm.settingsControls.open', false);
    const game = useGame();
    const settings = useSettings();

    useEffect(() => {
        // Get debugs
        const newDebugs = Object.keys(settings)
            .sort()
            .filter(name => name.startsWith('debug'))
            .map(name => ({
                label: name.replace('debug', ''),
                name,
                reloader: settings.reloaders.includes(name),
            }));
        setDebugs(newDebugs);

        // Get tests names
        const testNames = Object.keys(setup.tests);
        const newEventlessTestNames = testNames.filter(testName => !setup.events[testName]);
        setEventlessTestNames(newEventlessTestNames);
        const newEventTestNames = testNames.filter(testName => setup.events[testName]);
        setEventTestNames(newEventTestNames);
    }, [settings]);

    function handleChange(e: any) {
        let { name, type, value } = e.target;

        if (type === 'checkbox') {
            value = e.target.checked;
        } else if (type === 'number') {
            value = value ? parseFloat(value) : 1;
        }

        settings.set(name, value);
    }

    return (
        <>
            <Box bottom={0} left={0} position="fixed" zIndex={1200}>
                <IconButton aria-label={`${open ? 'close' : 'open'} settings`} onClick={() => setOpen(!open)}>
                    <Settings />
                </IconButton>
            </Box>

            {(open || settings.debugStats) && (
                <Box
                    bgcolor="white"
                    borderRadius={3}
                    bottom={-16}
                    left={60}
                    paddingBottom={3}
                    paddingX={2}
                    paddingTop={1}
                    position="fixed"
                    right={60}
                    zIndex={1200}
                >
                    {settings.debugStats && (
                        <>
                            <SettingsStats />
                            {open && (
                                <>
                                    <Box marginY={2}>
                                        <Divider />
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    {open && (
                        <Box display="flex" flexDirection="row" gap={2}>
                            <Box display="flex" flex={2} flexDirection="column" gap={1}>
                                <Typography paragraph>Game</Typography>
                                {eventlessTestNames && eventTestNames && (
                                    <TextField
                                        fullWidth
                                        label="Test*"
                                        name="test"
                                        select
                                        value={settings.test || ''}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        <Divider />
                                        {eventlessTestNames.map((testName, index) => (
                                            <MenuItem key={index} value={testName}>
                                                {testName}
                                            </MenuItem>
                                        ))}
                                        <Divider />
                                        {eventTestNames.map((testName, index) => (
                                            <MenuItem key={index} value={testName}>
                                                {testName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                <TextField
                                    inputProps={{ max: 1, min: 0.01, step: 0.01 }}
                                    label="Volume"
                                    name="volume"
                                    type="number"
                                    value={settings.volume}
                                    onChange={handleChange}
                                />
                            </Box>

                            <Box>
                                <Divider orientation="vertical" />
                            </Box>

                            <Box display="flex" flex={1} flexDirection="column" gap={1}>
                                <Typography paragraph>Boost</Typography>
                                <Button onClick={() => game.addCalories(1000)}>Calories</Button>
                            </Box>

                            <Box>
                                <Divider orientation="vertical" />
                            </Box>

                            <Box display="flex" flex={1} flexDirection="column" gap={1}>
                                <Typography paragraph>Snapshot</Typography>
                                <Button onClick={() => game.takeSnapshot()}>Take</Button>
                                <Button onClick={() => game.restoreSnapshot()}>Restore</Button>
                            </Box>

                            <Box>
                                <Divider orientation="vertical" />
                            </Box>

                            <Box flex={5}>
                                <Typography paragraph>Debug</Typography>
                                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr 1fr">
                                    {debugs.map((input, index) => (
                                        <Box key={index}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={settings[input.name]}
                                                        name={input.name}
                                                        value=""
                                                        onChange={handleChange}
                                                    />
                                                }
                                                label={`${input.label}${input.reloader ? '*' : ''}`}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
}
